import axios from "axios";

// ---------------------------------------------------------------------------
// Procore rate limiting (Sept 2026)
//
// Procore enforces an hourly limit AND a short "spike" limit on bursts.
// Its 429 responses carry no CORS headers, so in the browser a 429 shows up
// as a network error with no response object. We therefore treat
// "no response" the same as a 429: pause everything, slow down, retry.
// ---------------------------------------------------------------------------

// How many requests may be in flight at once (the pacer below sets the rate).
export const MAX_CONCURRENT_REQUESTS = 2;

const MIN_GAP_MS = 400; // fastest allowed spacing (~2.5/sec). Procore reported x-rate-limit-limit: 25 per window (9/2026)
const START_GAP_MS = 450; // starting spacing (~2.2/sec)
const MAX_GAP_MS = 2000; // slowest spacing after repeated throttling
const MAX_RETRIES = 5;

const pacer = {
  gap: START_GAP_MS,
  nextSlot: 0,
  pausedUntil: 0,
  successes: 0,
};

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Wait for this request's turn, keeping requests at least `gap` ms apart
// and honoring any pause triggered by throttling.
const acquireSlot = async () => {
  for (;;) {
    const now = Date.now();
    const start = Math.max(now, pacer.nextSlot, pacer.pausedUntil);
    if (start <= now) {
      pacer.nextSlot = now + pacer.gap;
      return;
    }
    await wait(start - now);
  }
};

const readRateHeaders = (response) => {
  const h = (response && response.headers) || {};
  const remaining = parseInt(h["x-rate-limit-remaining"], 10);
  const reset = parseInt(h["x-rate-limit-reset"], 10);
  return { remaining, reset };
};

const onSuccess = (response) => {
  pacer.successes++;
  // speed back up gradually after a run of successes
  if (pacer.successes % 10 === 0) {
    pacer.gap = Math.max(MIN_GAP_MS, Math.round(pacer.gap * 0.9));
  }
  // If Procore exposes its counters to the browser, respect them.
  const { remaining, reset } = readRateHeaders(response);
  if (!isNaN(remaining) && remaining <= 2 && !isNaN(reset)) {
    pacer.pausedUntil = Math.max(pacer.pausedUntil, reset * 1000 + 250);
  }
};

const onThrottled = (attempt, response) => {
  pacer.successes = 0;
  pacer.gap = Math.min(MAX_GAP_MS, pacer.gap * 2);
  const { reset } = readRateHeaders(response);
  const backoff = !isNaN(reset)
    ? reset * 1000 - Date.now() + 250
    : Math.min(30000, 2000 * Math.pow(2, attempt - 1)) + Math.random() * 500;
  pacer.pausedUntil = Math.max(pacer.pausedUntil, Date.now() + backoff);
};

// 429s look like "no response" in the browser (see note above)
const isThrottled = (error) =>
  (error.response && error.response.status === 429) ||
  (!error.response && !!error.request);

export const installRateLimitRetry = () => {
  axios.interceptors.request.use(async (config) => {
    await acquireSlot();
    return config;
  });
  axios.interceptors.response.use(
    (response) => {
      onSuccess(response);
      return response;
    },
    async (error) => {
      const config = error.config;
      if (!config || !isThrottled(error)) throw error;
      config.__retryCount = (config.__retryCount || 0) + 1;
      if (config.__retryCount > MAX_RETRIES) throw error;
      onThrottled(config.__retryCount, error.response);
      return axios(config); // re-enters the pacer before being sent
    }
  );
};

// Runs `task(item)` for every item, never more than `limit` at a time.
// `shouldContinue` lets a newer search cancel the rest of an older one.
export const runLimited = async (items, limit, task, shouldContinue = () => true) => {
  let next = 0;
  const worker = async () => {
    while (next < items.length && shouldContinue()) {
      const item = items[next++];
      await task(item);
    }
  };
  const workers = [];
  for (let w = 0; w < Math.min(limit, items.length); w++) workers.push(worker());
  await Promise.all(workers);
};
